import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { track } from '@/lib/analytics';

/**
 * Automatically tracks page views and time-on-page.
 *
 * Drop this hook into your root layout component (App.tsx or a layout wrapper).
 * It fires a `page_viewed` event whenever the route changes, including the
 * previous page's duration_ms.
 *
 * Usage:
 *   function App() {
 *     usePageTracking();
 *     ...
 *   }
 */
export function usePageTracking(): void {
  const { pathname } = useLocation();
  const enteredAtRef = useRef<number>(Date.now());
  const prevPathRef = useRef<string | null>(null);

  useEffect(() => {
    const now = Date.now();

    // Fire duration for the PREVIOUS page
    if (prevPathRef.current !== null) {
      const durationMs = now - enteredAtRef.current;
      track('page_viewed', {
        page: prevPathRef.current,
        duration_ms: durationMs,
      });
    }

    // Reset for current page
    prevPathRef.current = pathname;
    enteredAtRef.current = now;
  }, [pathname]);

  // Track the final page on unmount / tab close
  useEffect(() => {
    const handleUnload = () => {
      if (prevPathRef.current !== null) {
        const durationMs = Date.now() - enteredAtRef.current;
        track('page_viewed', {
          page: prevPathRef.current,
          duration_ms: durationMs,
        });
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);
}
